import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api } from '../api';
import { Field, Problems } from '../components/Field';
import { dayLabel, todayLocal } from '../dates';
import { describe } from '../errors';
import { refreshAll, usePortalLoad } from './offline';
import { Actions, LoadFailure, SavedNote } from './shared';

interface Entry {
  id: number;
  kind: string;
  start_date: string;
  end_date: string | null;
  byweekday: string;
  all_day: boolean;
  start_time: string | null;
  end_time: string | null;
  reason: string;
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const describeEntry = (e: Entry) => {
  const weekdays = e.byweekday
    .split(',')
    .filter(Boolean)
    .map((d) => DAYS[Number(d) - 1])
    .join(', ');
  const until = e.end_date ? ` to ${dayLabel(e.end_date)}` : '';
  const when =
    e.kind === 'recurring'
      ? `Every ${weekdays} from ${dayLabel(e.start_date)}${until}`
      : e.end_date && e.end_date !== e.start_date
        ? `${dayLabel(e.start_date)}${until}`
        : dayLabel(e.start_date);
  const hours = e.all_day ? '' : `, ${e.start_time?.slice(0, 5)}–${e.end_time?.slice(0, 5)}`;
  return `${when}${hours}`;
};

/** The body that recreates an entry: how Undo puts back what was removed. */
const bodyOf = (e: Entry) => ({
  kind: e.kind,
  start_date: e.start_date,
  end_date: e.end_date,
  ...(e.kind === 'recurring' ? { frequency: 'weekly', byweekday: e.byweekday } : {}),
  all_day: e.all_day,
  start_time: e.all_day ? null : (e.start_time?.slice(0, 5) ?? null),
  end_time: e.all_day ? null : (e.end_time?.slice(0, 5) ?? null),
  reason: e.reason,
});

/** Changing time off needs a signal: unlike a visit, it is not something to queue and hope. */
export function PortalTimeOff() {
  // After a change every screen reloads, so the schedule shows the days off straight away.
  const load = usePortalLoad<{ items: Entry[] }>('/api/portal/unavailability');
  const { data } = load;
  const [adding, setAdding] = useState(false);
  const [done, setDone] = useState<{ text: string; undo?: Entry } | null>(null);
  const firstField = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    if (adding) firstField.current?.focus();
  }, [adding]);
  const [kind, setKind] = useState<'range' | 'recurring'>('range');
  const [start, setStart] = useState(todayLocal());
  const [end, setEnd] = useState('');
  const [days, setDays] = useState<number[]>([]);
  const [allDay, setAllDay] = useState(true);
  const [from, setFrom] = useState('09:00');
  const [to, setTo] = useState('17:00');
  const [reason, setReason] = useState('');
  const [problems, setProblems] = useState<string[]>([]);

  async function add(e: FormEvent) {
    e.preventDefault();
    setProblems([]);
    setDone(null);
    const weekdays = [...days].sort().join(',');
    try {
      await api('POST', '/api/portal/unavailability', {
        kind,
        start_date: start,
        end_date: end || null,
        ...(kind === 'recurring' ? { frequency: 'weekly', byweekday: weekdays } : {}),
        all_day: allDay,
        start_time: allDay ? null : from,
        end_time: allDay ? null : to,
        reason,
      });
      const added = describeEntry({
        id: 0,
        kind,
        start_date: start,
        end_date: end || null,
        byweekday: kind === 'recurring' ? weekdays : '',
        all_day: allDay,
        start_time: from,
        end_time: to,
        reason,
      });
      setReason('');
      setEnd('');
      setAdding(false);
      setDone({ text: `Added: ${added}.` });
      refreshAll();
    } catch (err) {
      setProblems(describe(err).problems);
    }
  }

  async function remove(entry: Entry) {
    setProblems([]);
    setDone(null);
    try {
      await api('DELETE', `/api/portal/unavailability/${entry.id}`);
      setDone({ text: `Removed: ${describeEntry(entry)}.`, undo: entry });
      refreshAll();
    } catch (err) {
      setProblems(describe(err).problems);
    }
  }

  async function undo(entry: Entry) {
    setProblems([]);
    try {
      await api('POST', '/api/portal/unavailability', bodyOf(entry));
      setDone({ text: `Put back: ${describeEntry(entry)}.` });
      refreshAll();
    } catch (err) {
      setProblems(describe(err).problems);
    }
  }

  // Said beside what was pressed, the form's button or the list, and moved to so it is read out.
  const problemsBox = <Problems problems={problems} title="Could not do that." focus />;

  const head = (
    <>
      <h1>Time off</h1>
      <p className="muted p-sub">
        Days you cannot work. You will not be offered work that falls on them.
      </p>
    </>
  );
  if (!data) {
    return (
      <>
        {head}
        {load.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <LoadFailure what="your time off" load={load} />
        )}
      </>
    );
  }

  return (
    <>
      {head}
      <SavedNote load={load} extra="You need a signal to change it." />
      {data.items.length === 0 && <p className="empty">You have not marked any time off.</p>}
      <ul className="p-list">
        {data.items.map((e) => (
          <li key={e.id} className="p-visit p-timeoff">
            <strong>{describeEntry(e)}</strong>
            {e.reason && <span className="muted">{e.reason}</span>}
            <Actions>
              <button type="button" className="quiet" onClick={() => void remove(e)}>
                Remove <span className="sr-only">{describeEntry(e)}</span>
              </button>
            </Actions>
          </li>
        ))}
      </ul>
      {!adding && problemsBox}
      {done && (
        <div role="status" className="p-done p-done-row">
          <span>{done.text}</span>
          {done.undo && (
            <Actions>
              <button type="button" className="quiet" onClick={() => void undo(done.undo!)}>
                Undo
              </button>
            </Actions>
          )}
        </div>
      )}

      {!adding && (
        <Actions>
          <button type="button" className="p-big" onClick={() => setAdding(true)}>
            Add time off
          </button>
        </Actions>
      )}
      {adding && (
        <Actions>
          <form onSubmit={add} className="stack card" noValidate aria-labelledby="add-h">
            <h2 id="add-h">Add time off</h2>
            <Field label="Type">
              <select
                ref={firstField}
                value={kind}
                onChange={(e) => setKind(e.target.value as 'range' | 'recurring')}
              >
                <option value="range">Particular dates</option>
                <option value="recurring">Every week</option>
              </select>
            </Field>
            <Field label={kind === 'range' ? 'First day' : 'Starting'}>
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            </Field>
            <Field
              label={kind === 'range' ? 'Last day (leave blank for one day)' : 'Until (optional)'}
            >
              <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            </Field>
            {kind === 'recurring' && (
              <fieldset className="p-weekdays">
                <legend>On these days</legend>
                {DAYS.map((d, i) => (
                  <label key={d} className="check">
                    <input
                      type="checkbox"
                      checked={days.includes(i + 1)}
                      onChange={(e) =>
                        setDays(
                          e.target.checked ? [...days, i + 1] : days.filter((x) => x !== i + 1),
                        )
                      }
                    />
                    {d}
                  </label>
                ))}
              </fieldset>
            )}
            <label className="check">
              <input
                type="checkbox"
                checked={allDay}
                onChange={(e) => setAllDay(e.target.checked)}
              />
              All day
            </label>
            {!allDay && (
              <div className="row">
                <Field label="From">
                  <input type="time" value={from} onChange={(e) => setFrom(e.target.value)} />
                </Field>
                <Field label="To">
                  <input type="time" value={to} onChange={(e) => setTo(e.target.value)} />
                </Field>
              </div>
            )}
            <Field label="Reason (optional, for the office)">
              <input value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} />
            </Field>
            {problemsBox}
            <div className="row">
              <button type="submit">Add time off</button>
              <button type="button" className="quiet" onClick={() => setAdding(false)}>
                Cancel
              </button>
            </div>
          </form>
        </Actions>
      )}
    </>
  );
}
